import { FC, useState } from 'react'

import CustomNumericFormat from '@/components/base/CustomNumericFormat'
import {
  Avatar,
  Box,
  Divider,
  InputBase,
  Stack,
  Typography,
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { format } from '../../utils/number-helpers'

type BaseInputAmountParams = {
  amount: string
  setAmount: (value: string) => void
  tokenAvatar: string
  tokenSymbol: string
  checkStakingBalance?: boolean
  balance?: number
  minAmount?: number
}
const AmountInput: FC<BaseInputAmountParams> = ({
  amount,
  setAmount,
  tokenAvatar,
  tokenSymbol,
  checkStakingBalance = false,
  balance = 0,
  minAmount = 0,
}) => {
  const [error, setError] = useState('')
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setAmount(value.toString())

    if (Number(value) < 0) {
      setError('Amount cannot be negative')
    } else if (value === '') {
      setError('Amount is required')
    } else if (Number(value) < minAmount) {
      setError(`Min amount: ${minAmount} ${tokenSymbol}`)
    } else if (Number(value) > balance) {
      setError('Insufficient balance')
    } else {
      setError('')
    }
  }

  return (
    <Box className="px-4 pb-4 bg-transparent-light-purple-4 rounded-lg">
      <Stack>
        <Stack gap={1} className="py-3">
          <Typography className="text-white font-dm_mono_regular text-xs-2 sm:text-sm lg:text-base">
            Amount
          </Typography>
          <Stack
            justifyContent="space-between"
            alignItems="center"
            direction="row"
            gap={1}
            height="2.5rem"
          >
            <InputBase
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              inputComponent={CustomNumericFormat as any}
              placeholder="Enter amount"
              className="p-0 p-xl font-dm_mono_regular"
              fullWidth
              value={amount}
              onChange={handleAmountChange}
              inputProps={{
                style: { color: '#ffffff' },
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '14px', sm: '16px', lg: '20px' }, // Responsive font sizes
                  lineHeight: {
                    xs: '20px',
                    sm: '24px',
                    lg: '28px',
                  }, // Responsive line heights
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
              }}
            />
            <Stack direction="row" alignItems="center" gap={1}>
              <Avatar
                src={tokenAvatar}
                alt={tokenSymbol}
                sx={{
                  width: 32,
                  height: 32,
                }}
              />
              <Typography className="text-white text-xs-2 sm:text-sm font-dm_mono_regular">
                {tokenSymbol}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Divider
          className="w-full "
          style={{
            height: '2px',
            backgroundColor: error ? '#ff7171' : 'rgba(255, 255, 255, 0.08)',
          }}
        />
        <Stack className="mt-3">
          <Stack justifyContent="space-between" direction="row" gap={1}>
            <Stack direction="row">
              <Typography className="text-white/50 font-normal text-xs-2 sm:text-sm font-dm_mono_regular">
                {checkStakingBalance
                  ? 'Staking balance:'
                  : 'Available balance:'}
              </Typography>
              <Typography className="text-white text-xs-2 sm:text-sm font-medium font-dm_mono_regular">
                &nbsp;{format(balance, true)}&nbsp;{tokenSymbol}
              </Typography>
            </Stack>
            <Typography
              className="text-white/50 text-xs-2 sm:text-sm font-normal font-dm_mono_regular underline cursor-pointer"
              onClick={() => setAmount(balance.toString())}
            >
              Enter max
            </Typography>
          </Stack>
          {error && (
            <Stack direction="row" className="gap-1 pt-2 flex items-center">
              <ErrorOutlineIcon
                sx={{
                  color: '#FF7373',
                  width: { xs: 15, sm: 20 },
                  height: { xs: 15, sm: 20 },
                }}
              />
              <Typography className="text-warm-red text-xs-2 sm:text-sm font-dm_mono_regular">
                {error}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

export default AmountInput
